import { useFormik } from "formik"
import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { array, number, object, ObjectSchema, string } from "yup"

import { TStore } from "../../../../store/Store"
import { DropdownObj } from "../../../../pages/Common/commonTypes"
import { EFormModule, FormPageProps } from "../.."
import { IDealSowForm, setDealSowForm, setDealTemplate, setTemplateId } from "../../RaiseTicketFormSlice"
import {
    DEAL_TEMPLATES,
    DEAL_TEMPLATES_OPTIONS,
    SCOPE_OF_WORK,
    SCOPE_OF_WORK_OPTIONS,
} from "../../config/projectFormConstants"
import { useGetSOWTemplatesQuery } from "pages/Projects/ProjectServices"

const schema: ObjectSchema<IDealSowForm> = object({
    deal_template: object({
        id: number().required(),
        title: string().required(),
    }).required(),
    deal_type: object({
        id: number().required(),
        title: string().required(),
    }).required(),
    deal_scope_of_work: array()
        .of(
            object({
                id: number().required(),
                title: string().required(),
            })
        )
        .required(),
    template_id: number(),
})

const useRaiseTicketDealDetails = (props: FormPageProps) => {
    const dispatch = useDispatch()
    const { deal_sow_form, form_module } = useSelector((state: TStore) => state.RaiseTicketForm)

    const [dealTemplateOptions, setDealTemplateOptions] = useState<DropdownObj[]>(DEAL_TEMPLATES_OPTIONS)
    const [dealScopeOfWorkOptions, setDealScopeOfWorkOptions] = useState<DropdownObj[]>(SCOPE_OF_WORK_OPTIONS)
    const { data: sowTemplates } = useGetSOWTemplatesQuery({ module_id: 3 })

    const isProjectsModule = form_module === EFormModule.PROJECTS
    const isSurveysModule = form_module === EFormModule.SURVEYS
    const isLogisticsModule = form_module === EFormModule.LOGISTICS
    const isDemolitionModule = form_module === EFormModule.DEMOLITION
    const isEvApplicationModule = form_module === EFormModule.EV_APPLICATION

    const isDealDetailsDisabled = isSurveysModule || isLogisticsModule || isDemolitionModule || isEvApplicationModule

    const formik = useFormik({
        initialValues: deal_sow_form,
        onSubmit: handleSubmit,
        enableReinitialize: true,
        validationSchema: schema,
    })

    const handleCheckboxChange = (selectedValues: DropdownObj[]) => {
        dispatch(
            setDealSowForm({
                value: selectedValues,
                key: "deal_scope_of_work",
            })
        )
    }

    const handleDealTemplateChange = (value: DropdownObj) => {
        dispatch(setDealTemplate(value))
    }

    const handleDealTypeChange = (value: DropdownObj) => {
        formik.setFieldValue("deal_type", value)

        if (form_module === EFormModule.LOGISTICS && sowTemplates) {
            const template = sowTemplates.find(
                template => template.deal_type.toLowerCase() === value.title.toLowerCase()
            )
            if (template?.template_id) {
                formik.setFieldValue("template_id", template.template_id)
                dispatch(setTemplateId(template.template_id))
            }
        }

        dispatch(
            setDealSowForm({
                key: "deal_type",
                value,
            })
        )
    }

    function handleSubmit(values: IDealSowForm) {
        if (props.module === EFormModule.PROJECTS) {
            dispatch(
                setDealSowForm({
                    value: values.deal_type,
                    key: "deal_type",
                })
            )
        }
        props.handleNextStep()
    }


    useEffect(() => {
        if (isLogisticsModule) {
            handleDealTemplateChange({ id: DEAL_TEMPLATES.SOLO_LOGISTICS, title: "Solo Logistics" })
            handleCheckboxChange([{ id: SCOPE_OF_WORK.SOLO_LOGISTICS, title: "Solo Logistics" }])
        } else if (isSurveysModule) {
            handleDealTemplateChange({ id: DEAL_TEMPLATES.SOLO_SURVEY, title: "Solo Survey" })
            handleCheckboxChange([{ id: SCOPE_OF_WORK.SOLO_SURVEY, title: "Solo Survey" }])
        } else if (isDemolitionModule) {
            handleDealTemplateChange({ id: DEAL_TEMPLATES.DEMOLITION, title: "Demolition" })
            handleCheckboxChange([{ id: SCOPE_OF_WORK.DEMOLITION, title: "Demolition" }])
        } else if (isEvApplicationModule) {
            handleDealTemplateChange({ id: DEAL_TEMPLATES.EV_INFRA, title: "EV Infra" })
        }

        if (isProjectsModule) {
            setDealTemplateOptions(prev =>
                prev.filter(
                    option =>
                        option.id !== DEAL_TEMPLATES.SOLO_LOGISTICS &&
                        option.id !== DEAL_TEMPLATES.SOLO_SURVEY &&
                        option.id !== DEAL_TEMPLATES.DEMOLITION
                )
            )
            setDealScopeOfWorkOptions(prev =>
                prev.filter(
                    option =>
                        option.id !== SCOPE_OF_WORK.SOLO_LOGISTICS &&
                        option.id !== SCOPE_OF_WORK.SOLO_SURVEY &&
                        option.id !== SCOPE_OF_WORK.DEMOLITION
                )
            )
        }
    }, [isSurveysModule, isLogisticsModule, isProjectsModule, isEvApplicationModule, isDemolitionModule])

    return {
        states: {
            formik,
            dealTemplateOptions,
            isDealDetailsDisabled,
            deal_sow_form,
            dealScopeOfWorkOptions,
        },
        functions: {
            dispatch,
            handleSubmit,
            handleDealTemplateChange,
            handleCheckboxChange,
            handleDealTypeChange,
        },
    }
}

export default useRaiseTicketDealDetails
